import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"
import PreferenceContext from "hooks/preferences"
import { useAccountId, useAccountAlias } from "resources/account"
import Loading from "components/loading"
import { DashboardIdProvider, useDashboardId } from "./context/dashboard-id"
import { useCurrentDashboardId } from "./context/current-dashboard-id"
import { getDashboardPreferenceKey, getDashboardPreferenceFallbackKey } from "./helpers/preference-keys"
import { useDashboardPreferencesSync } from "./hooks/use-dashboard-preferences-sync"
import { useModelId } from "./hooks/use-model-id"
import { useModelSync } from "./hooks/use-model-sync"
import TradeSyncEffect from "./components/trade-sync-effect"
import { useWorkspaceSync, type GetArrangement } from "./hooks/use-workspace-sync"
import { useLazyMigration } from "./hooks/use-lazy-migration"
import { useInstanceWorkspaceContext } from "./context/instance-workspace-context"

type Arrangement = ReturnType<GetArrangement>

// Runs the single workspace writer inside the InstanceWorkspaceProvider so it can read the live
// Dockview arrangement getter (populated by the renderer) at flush time. No-ops when flag off.
function WorkspaceSyncRunner({ dashboardId, canPersist }: { dashboardId: string, canPersist: boolean }) {
  const context = useInstanceWorkspaceContext()
  // The engine clears its getter on unmount BEFORE this writer's flush runs, so cache the last live
  // arrangement — else the unmount flush falls back to the seeded one and loses an in-session drag.
  const lastLiveArrangementRef = useRef<Arrangement>(null)
  const getArrangement = useCallback<GetArrangement>(() => {
    const live = context?.arrangementGetterRef.current?.()
    if (live) {
      lastLiveArrangementRef.current = live
      return live
    }
    // Getter gone: prefer the last live arrangement we captured (e.g. a drag), and fall back to the
    // seed only when the engine never wired (no live ever seen) — never clobber a real change.
    return lastLiveArrangementRef.current ?? (context?.currentSeed?.arrangement as Arrangement | undefined) ?? null
  }, [context])
  const requestSave = useWorkspaceSync(dashboardId, getArrangement, canPersist)
  // Expose the save trigger so the layout engine can flush a Dockview arrangement change (which
  // doesn't dispatch to Redux) through the workspace writer.
  useEffect(() => {
    if (!context) return undefined
    context.requestSaveRef.current = requestSave
    return () => {
      context.requestSaveRef.current = null
    }
  }, [context, requestSave])
  return null
}

// Instance-mode gate: holds the sync hooks (in DashboardPreferencesSyncGate's subtree) until the slices are
// seeded, so they treat the seed as authoritative. Renders <Loading/> until the seed completes so
// render 0 never flashes defaults. Flag-off short-circuits.
function InstanceWorkspaceGate({ children }: { children: ReactNode }) {
  const dashboardId = useDashboardId()
  const instanceWorkspace = useInstanceWorkspaceContext()

  if (!instanceWorkspace) {
    return children
  }

  if (!instanceWorkspace.currentSeed) {
    return <Loading />
  }

  return (
    <>
      <WorkspaceSyncRunner dashboardId={dashboardId} canPersist={instanceWorkspace.currentSeed.loadSucceeded} />
      {children}
    </>
  )
}

// Lazy migration (APP-3155): on first flag-on load of a legacy dashboard, import its hydrated state
// as a layout + instance and replace-navigate to it. Mounts inside DashboardPreferencesSyncGate (after
// isStoreLoaded) so the migration reads fully-hydrated slices; flag-off / real-instance ids "skip".
function LazyMigrationGate({ dashboardId, children }: { dashboardId: string, children: ReactNode }) {
  const accountAlias = useAccountAlias()
  const { search } = useLocation()
  const result = useLazyMigration(dashboardId)

  if (result.status === "pending") {
    return <Loading />
  }

  // Routes are account-scoped, so the alias prefix is required (else 404). Route to the slug; the
  // resolver maps it back to the canonical instanceId. `search` preserves a deep-link instrument
  // across the hop. [APP-3328]
  if (result.status === "migrated" && result.instanceId !== dashboardId) {
    return <Navigate to={`/${accountAlias}/dashboard/${result.slug}${search}`} replace />
  }

  return children
}

function DashboardPreferencesSyncGate({ children }: { children: ReactNode }) {
  const isStoreLoaded = useDashboardPreferencesSync()
  const modelId = useModelId()

  // One model-sync per dashboard — keep global home layout in sync with modelId.
  useModelSync(modelId)

  if (!isStoreLoaded) {
    return <Loading />
  }

  return (
    <>
      <TradeSyncEffect />
      {children}
    </>
  )
}

function KeyedDashboardContent({ dashboardId, children }: { dashboardId: string, children: ReactNode }) {
  const accountId = useAccountId()

  const preferencesProps = useMemo(() => ({
    accountId,
    key: getDashboardPreferenceKey(dashboardId),
    fallbackKey: getDashboardPreferenceFallbackKey(dashboardId),
    type: "dashboard",
  }), [accountId, dashboardId])

  return (
    <PreferenceContext.Provider value={preferencesProps}>
      <DashboardIdProvider value={dashboardId}>
        <InstanceWorkspaceGate>
          <DashboardPreferencesSyncGate>
            <LazyMigrationGate dashboardId={dashboardId}>
              {children}
            </LazyMigrationGate>
          </DashboardPreferencesSyncGate>
        </InstanceWorkspaceGate>
      </DashboardIdProvider>
    </PreferenceContext.Provider>
  )
}

export default function DashboardContent({ children }: { children: ReactNode }) {
  const dashboardId = useCurrentDashboardId()

  if (dashboardId === null) {
    return <Loading />
  }

  // key forces a remount when the route param changes: React Router keeps the element mounted
  // across param-only navigations, but the preference sync hooks require a stable dashboardId
  // for their lifetime (one-shot load refs in usePreferences).
  return (
    <KeyedDashboardContent key={dashboardId} dashboardId={dashboardId}>
      {children}
    </KeyedDashboardContent>
  )
}
